import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { SiteFooter } from "@/components/SiteFooter";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ firstName: "", lastName: "", message: "" });
  const [dataConsent, setDataConsent] = useState(false);
  const [dataLabel, setDataLabel] = useState("J'accepte que mes données (nom, prénom et le contenu de mon message) soient traitées par la pharmacie uniquement pour répondre à ma demande. Conservation limitée à 3 ans, aucun partage à des fins commerciales. Je peux exercer mes droits (accès, rectification, effacement).");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/consent/label/donnees_contact").then((r) => setDataLabel(r.data.texte)).catch(() => {});
  }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await api.post("/contact", { ...form, dataConsent });
      setSent(true);
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || "Envoi impossible."); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <main className="flex-1 mx-auto max-w-xl w-full px-4 py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground mb-6 hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Accueil</Link>
        <div className="flex items-center gap-2.5 mb-6">
          <div className="h-10 w-10 rounded-xl bg-primary grid place-items-center text-primary-foreground"><Mail className="h-5 w-5" /></div>
          <div><h1 className="font-heading text-2xl font-bold">Contact</h1><p className="text-xs text-muted-foreground">Pharmacie des Écoles</p></div>
        </div>

        {sent ? (
          <Card className="rounded-2xl p-8 text-center" data-testid="contact-success">
            <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-3" />
            <h2 className="font-heading font-semibold text-lg mb-1">Message envoyé</h2>
            <p className="text-sm text-muted-foreground">Merci, nous avons bien reçu votre message. Pour une réponse rapide, appelez-nous au <span className="font-medium text-foreground">04 94 66 81 57</span> ou passez à la pharmacie.</p>
          </Card>
        ) : (
          <Card className="rounded-2xl p-6">
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label htmlFor="ln">Nom *</Label><Input id="ln" required data-testid="contact-lastname" value={form.lastName} onChange={set("lastName")} className="h-11 rounded-xl" /></div>
                <div className="space-y-1.5"><Label htmlFor="fn">Prénom *</Label><Input id="fn" required data-testid="contact-firstname" value={form.firstName} onChange={set("firstName")} className="h-11 rounded-xl" /></div>
              </div>
              <div className="space-y-1.5"><Label htmlFor="msg">Message *</Label><Textarea id="msg" required rows={5} data-testid="contact-message" value={form.message} onChange={set("message")} className="rounded-xl" /></div>

              <div className="flex items-start gap-2.5 rounded-xl border p-3 border-primary/40 bg-primary/5">
                <Checkbox id="data-consent" data-testid="contact-data-consent" checked={dataConsent} onCheckedChange={(v) => setDataConsent(!!v)} className="mt-0.5" />
                <Label htmlFor="data-consent" className="text-sm font-normal leading-snug cursor-pointer">{dataLabel} <span className="text-destructive">*</span></Label>
              </div>
              {error && <p className="text-sm text-destructive" data-testid="contact-error">{error}</p>}
              <Button type="submit" disabled={loading || !dataConsent} data-testid="contact-submit" className="w-full h-12 rounded-2xl">{loading ? "Envoi…" : "Envoyer la demande"}</Button>

              <p className="text-xs text-muted-foreground leading-relaxed">
                Les informations recueillies sont traitées par Pharmacie des Écoles pour répondre à votre demande.
                Elles sont conservées 3 ans à compter du dernier contact. Aucune adresse e-mail ni numéro de téléphone
                n'est demandé. Vous pouvez accéder à vos données, les rectifier, les supprimer ou vous opposer à leur
                traitement par courrier à la pharmacie ou via ce formulaire.
                En savoir plus : <Link to="/confidentialite" className="underline hover:text-foreground">Politique de confidentialité</Link>.
              </p>
            </form>
          </Card>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
